"use client";

import { motion, useAnimation, useReducedMotion, Variants } from "framer-motion";
import { forwardRef, useCallback, useImperativeHandle, useRef } from "react";

export interface LayoutListIconHandle {
  startAnimation: () => void;
  stopAnimation: () => void;
}

interface LayoutListIconProps {
  size?: number;
  duration?: number;
  isAnimated?: boolean;
  isHovered?: boolean;
  className?: string;
}

const LayoutListIcon = forwardRef<LayoutListIconHandle, LayoutListIconProps>(
  (
    {
      className,
      size = 24,
      duration = 1,
      isAnimated = true,
      isHovered = false,
    },
    ref,
  ) => {
    const controls = useAnimation();
    const reduced = useReducedMotion();
    const isControlled = useRef(false);
    const prevHovered = useRef(isHovered);

    useImperativeHandle(ref, () => {
      isControlled.current = true;
      return {
        startAnimation: () =>
          reduced ? controls.start("normal") : controls.start("animate"),
        stopAnimation: () => controls.start("normal"),
      };
    });

    // React to isHovered prop changes
    if (isHovered !== prevHovered.current) {
      prevHovered.current = isHovered;
      if (isHovered && isAnimated && !reduced) {
        controls.start("animate");
      } else if (!isHovered) {
        controls.start("normal");
      }
    }

    const handleEnter = useCallback(() => {
      if (!isAnimated || reduced) return;
      if (!isControlled.current) controls.start("animate");
    }, [controls, reduced, isAnimated]);

    const handleLeave = useCallback(() => {
      if (!isControlled.current) controls.start("normal");
    }, [controls]);

    const listVariants: Variants = {
      normal: { scale: 1, rotate: 0 },
      animate: {
        scale: [1, 1.03, 1],
        rotate: [0, 1, 0],
        transition: {
          duration: 0.6 * duration,
          ease: "easeInOut" as const,
        },
      },
    };

    const lineVariants: Variants = {
      normal: { opacity: 1, scaleX: 1 },
      animate: (i: number) => ({
        opacity: [0.4, 1],
        scaleX: [0.6, 1.05, 1],
        transition: {
          duration: 0.5 * duration,
          delay: 0.08 * i,
          ease: "easeOut" as const,
        },
      }),
    };

    return (
      <motion.svg
        xmlns="http://www.w3.org/2000/svg"
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className={className}
        onMouseEnter={handleEnter}
        onMouseLeave={handleLeave}
        style={{ flexShrink: 0 }}
        variants={listVariants}
        initial="normal"
        animate={controls}
      >
        <motion.rect
          width="7"
          height="7"
          x="3"
          y="3"
          rx="1"
          variants={lineVariants}
          custom={0}
          initial="normal"
          animate={controls}
        />
        <motion.rect
          width="7"
          height="7"
          x="3"
          y="14"
          rx="1"
          variants={lineVariants}
          custom={2}
          initial="normal"
          animate={controls}
        />
        <motion.path
          d="M14 4h7"
          variants={lineVariants}
          custom={0}
          initial="normal"
          animate={controls}
        />
        <motion.path
          d="M14 9h7"
          variants={lineVariants}
          custom={1}
          initial="normal"
          animate={controls}
        />
        <motion.path
          d="M14 15h7"
          variants={lineVariants}
          custom={2}
          initial="normal"
          animate={controls}
        />
        <motion.path
          d="M14 20h7"
          variants={lineVariants}
          custom={3}
          initial="normal"
          animate={controls}
        />
      </motion.svg>
    );
  },
);

LayoutListIcon.displayName = "LayoutListIcon";
export { LayoutListIcon };
